import {
  Alert,
  AlertDescription,
  AlertIcon,
  Badge,
  Button,
  Center,
  FormControl,
  FormErrorMessage,
  FormHelperText,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  NumberDecrementStepper,
  NumberIncrementStepper,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  Spinner,
  Switch,
  Text,
  Textarea,
  Tooltip,
  useInterval,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { Controller, useForm } from 'react-hook-form';
import { Sparkles } from 'lucide-react';
import { useState, useMemo, type ReactNode } from 'react';

import { trpcAdmin } from '@duke-hq/app-admin-web/lib/trpcAdmin';
import { ControlledSelect } from '@duke-hq/ui/components/Form/Select';
import { useCheckPermission } from '../../../hooks/useCheckPermission';
import { AccreditedUnitCodeField } from './AccreditedUnitCodeField';
import { BlockersAlert } from './ReviewLayout';
import { UnitRequirementsStep } from './UnitRequirementsStep';
import { testIds } from './testIds';
import {
  cleanUnitCode,
  EMPTY_REQUIREMENT_SET,
  nonAccreditedSourceUnit,
  requirementSetIssues,
  requirementsToReview,
  type SourceUnit,
  type RequirementSet,
} from '../reviewedRequirements';

type CreateRunFormData = {
  unitCode: string;
  unitTitle: string;
  description: string;
  contentProfileId: string;
  targetSectionWordCount: number;
};

type Step = 'details' | 'fetching' | 'drafting' | 'requirements';

function KindTab({
  isActive,
  onSelect,
  children,
  'data-testid': testId,
}: {
  isActive: boolean;
  onSelect: () => void;
  children: ReactNode;
  'data-testid'?: string;
}) {
  return (
    <Button
      type="button"
      flex={1}
      size="sm"
      variant="ghost"
      bg={isActive ? 'bg.surface' : undefined}
      shadow={isActive ? 'sm' : undefined}
      color={isActive ? 'brand.600' : 'fg.muted'}
      borderRadius="md"
      _hover={{ bg: isActive ? 'bg.surface' : 'bg.subtle' }}
      onClick={onSelect}
      data-testid={testId}
    >
      {children}
    </Button>
  );
}

export function CreateRunModal({ isOpen, onClose }: { isOpen: boolean; onClose: () => void }) {
  const trpcUtils = trpcAdmin.useUtils();
  const toast = useToast();
  const canUpdateNrtRegister = useCheckPermission('canAccessDeveloperTools');

  const { data: profiles = [] } = trpcAdmin.contentProfiles.all.useQuery(undefined, {
    enabled: isOpen,
  });

  const profileOptions = useMemo(
    () =>
      profiles.map(({ id, title, acsfLevel }) => ({
        label: (
          <HStack spacing={2} justify="space-between" w="100%">
            <Text noOfLines={1}>{title}</Text>
            <Badge
              colorScheme="gray"
              fontSize="xs"
              textTransform="uppercase"
              borderRadius="full"
              px={2}
              flexShrink={0}
            >
              <Trans>ACSF Level {acsfLevel}</Trans>
            </Badge>
          </HStack>
        ),
        value: id,
      })),
    [profiles],
  );

  const {
    handleSubmit,
    control,
    register,
    getValues,
    setValue,
    clearErrors,
    formState: { errors },
  } = useForm<CreateRunFormData>({
    defaultValues: {
      unitCode: '',
      unitTitle: '',
      description: '',
      contentProfileId: '',
      targetSectionWordCount: 1800,
    },
  });

  const [accredited, setAccredited] = useState(true);
  const [autoAssess, setAutoAssess] = useState(false);
  const [step, setStep] = useState<Step>('details');
  const [requirements, setRequirements] = useState<RequirementSet>(EMPTY_REQUIREMENT_SET);
  const [trainingGovUnit, setTrainingGovUnit] = useState<SourceUnit | null>(null);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);
  const [draftedFrom, setDraftedFrom] = useState<string | null>(null);
  const [draftStage, setDraftStage] = useState(0);

  useInterval(
    () => setDraftStage((stage) => Math.min(stage + 1, 2)),
    step === 'drafting' ? 10_000 : null,
  );

  const draftKey = ({ unitCode, unitTitle, description }: CreateRunFormData) =>
    JSON.stringify([unitCode, unitTitle, description, autoAssess]);

  const triggerMutation = trpcAdmin.unitsOfCompetency.triggerUnitGeneration.useMutation({
    onSuccess: () => {
      toast({ status: 'success', title: t`Unit generation started` });
      void trpcUtils.unitsOfCompetency.listGenerations.invalidate();
      onClose();
    },
    onError: (error) => {
      toast({
        status: 'error',
        title: t`Failed to start generation`,
        description: error.message,
      });
    },
  });

  const frameworkMutation = trpcAdmin.unitsOfCompetency.generateUnitFramework.useMutation();

  const selectAccredited = (next: boolean) => {
    if (next === accredited) {
      return;
    }

    setAccredited(next);
    setAutoAssess(false);
    setValue('unitCode', '');
    setRequirements(EMPTY_REQUIREMENT_SET);
    setDraftedFrom(null);
    setDraftError(null);
    clearErrors();
  };

  const selectAutoAssess = (next: boolean) => {
    setAutoAssess(next);

    if (next) {
      setRequirements((current) => ({ ...current, performanceEvidence: [] }));
    }
  };

  const fetchPreview = async (unitCode: string) => {
    setStep('fetching');
    setPreviewError(null);

    try {
      const fetched = await trpcUtils.unitsOfCompetency.previewUnitRequirements.fetch({
        unitCode,
      });

      setTrainingGovUnit(fetched);
      setRequirements(requirementsToReview(fetched));
      setStep('requirements');
    } catch (error) {
      setPreviewError(error instanceof Error ? error.message : t`Could not read training.gov`);
    }
  };

  const draftFramework = async (data: CreateRunFormData) => {
    setDraftError(null);
    setDraftStage(0);
    setStep('drafting');

    try {
      const framework = await frameworkMutation.mutateAsync({
        unitCode: data.unitCode,
        unitTitle: data.unitTitle,
        description: data.description,
        autoAssess,
      });

      setRequirements({ ...EMPTY_REQUIREMENT_SET, ...framework });
      setDraftedFrom(draftKey(data));
      setStep('requirements');
    } catch (error) {
      setDraftError(
        error instanceof Error ? error.message : t`The course framework could not be drafted.`,
      );
      setStep('details');
    }
  };

  const onContinue = (data: CreateRunFormData) => {
    if (step !== 'details') {
      return;
    }

    if (accredited) {
      void fetchPreview(data.unitCode);
      return;
    }

    if (data.description.trim() === '' || draftKey(data) === draftedFrom) {
      setStep('requirements');
    } else {
      void draftFramework(data);
    }
  };

  const onGenerate = () => {
    const details = getValues();

    const sourceUnit = accredited
      ? trainingGovUnit
      : nonAccreditedSourceUnit({
          unitCode: details.unitCode,
          unitTitle: details.unitTitle.trim(),
          description: details.description.trim() || details.unitTitle.trim(),
        });

    if (sourceUnit == null) {
      return;
    }

    triggerMutation.mutate({
      unitCode: details.unitCode,
      contentProfileId: details.contentProfileId,
      accredited,
      publishedUnit: sourceUnit,
      reviewedRequirements: requirements,
      options: {
        targetSectionWordCount: details.targetSectionWordCount,
        autoAssess,
      },
    });
  };

  const goBackToDetails = () => {
    setStep('details');
    setPreviewError(null);
    setDraftError(null);
  };

  const issues = requirementSetIssues(requirements, { autoAssess });
  const blocking = issues.blocking;

  return (
    <Modal isOpen={isOpen} onClose={onClose} size={step === 'requirements' ? '4xl' : 'lg'}>
      <ModalOverlay />
      <ModalContent as="form" onSubmit={handleSubmit(onContinue)}>
        <ModalHeader>
          {accredited ? (
            <Trans>Create a new Unit of Competency</Trans>
          ) : (
            <Trans>Create a new non-accredited unit</Trans>
          )}
          <Text fontSize="sm" fontWeight="normal" color="fg.subtle">
            {step === 'requirements' ? (
              <Trans>Step 2 of 2: Unit requirements</Trans>
            ) : (
              <Trans>Step 1 of 2: Unit details</Trans>
            )}
          </Text>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody display="flex" flexDirection="column" gap={4}>
          {step === 'details' ? (
            <>
              <HStack spacing={1} p={1} bg="bg.muted" borderRadius="lg">
                <KindTab isActive={accredited} onSelect={() => selectAccredited(true)}>
                  <Trans>Accredited</Trans>
                </KindTab>
                <KindTab
                  isActive={!accredited}
                  onSelect={() => selectAccredited(false)}
                  data-testid={testIds.nonAccreditedTab}
                >
                  <Trans>Non-accredited</Trans>
                </KindTab>
              </HStack>

              {accredited ? (
                <Controller
                  name="unitCode"
                  control={control}
                  rules={{ required: t`Unit code is required` }}
                  render={({ field }) => (
                    <AccreditedUnitCodeField
                      value={field.value}
                      onChange={field.onChange}
                      inputRef={field.ref}
                      canUpdateRegister={canUpdateNrtRegister}
                      errorMessage={errors.unitCode?.message}
                    />
                  )}
                />
              ) : (
                <>
                  <FormControl isRequired isInvalid={errors.unitCode != null}>
                    <FormLabel htmlFor="nonAccreditedUnitCode">
                      <Trans>Unit Code</Trans>
                    </FormLabel>
                    <Input
                      id="nonAccreditedUnitCode"
                      placeholder={t`e.g. HBH-BIZ-101`}
                      fontFamily="mono"
                      {...register('unitCode', {
                        required: t`Unit code is required`,
                        setValueAs: cleanUnitCode,
                      })}
                    />
                    {errors.unitCode != null ? (
                      <FormErrorMessage>{errors.unitCode.message}</FormErrorMessage>
                    ) : null}
                  </FormControl>

                  <FormControl isRequired isInvalid={errors.unitTitle != null}>
                    <FormLabel htmlFor="nonAccreditedUnitTitle">
                      <Trans>Unit Title</Trans>
                    </FormLabel>

                    <Input
                      id="nonAccreditedUnitTitle"
                      placeholder={t`e.g. Foundations of small business`}
                      {...register('unitTitle', { required: t`Unit title is required` })}
                    />

                    {errors.unitTitle != null ? (
                      <FormErrorMessage>{errors.unitTitle.message}</FormErrorMessage>
                    ) : null}
                  </FormControl>

                  <FormControl isInvalid={errors.description != null}>
                    <FormLabel htmlFor="nonAccreditedDescription">
                      <Trans>Describe your course</Trans>
                      <Text as="span" color="fg.muted" fontWeight="normal" ml={2}>
                        <Trans>Optional</Trans>
                      </Text>
                    </FormLabel>

                    <Textarea
                      id="nonAccreditedDescription"
                      rows={5}
                      placeholder={t`An introduction for people considering self-employment on how to spot and evaluate a small business idea. Cover where ideas come from, checking there is a real market, estimating start-up costs and break-even, and deciding whether to go ahead.`}
                      {...register('description', {
                        validate: (value) =>
                          value.trim() === '' ||
                          value.trim().length >= 20 ||
                          t`Say a little more about the course, at least a sentence.`,
                      })}
                    />

                    <FormHelperText>
                      <Trans>Drafts the framework for you. Leave blank to write your own.</Trans>
                    </FormHelperText>

                    {errors.description != null ? (
                      <FormErrorMessage>{errors.description.message}</FormErrorMessage>
                    ) : null}
                  </FormControl>

                  <FormControl>
                    <HStack justify="space-between" align="center">
                      <FormLabel htmlFor="autoAssess" mb={0}>
                        <Trans>Auto-assessable</Trans>
                      </FormLabel>
                      <Switch
                        id="autoAssess"
                        isChecked={autoAssess}
                        data-testid={testIds.autoAssessSwitch}
                        onChange={(event) => selectAutoAssess(event.target.checked)}
                      />
                    </HStack>
                    <FormHelperText>
                      <Trans>Only auto-assessable question types will be generated.</Trans>
                    </FormHelperText>
                  </FormControl>
                </>
              )}

              <ControlledSelect<CreateRunFormData>
                control={control}
                name="contentProfileId"
                label={t`Content Profile`}
                isRequired
                rules={{ required: t`Content profile is required` }}
                options={profileOptions}
              />

              <FormControl isRequired isInvalid={errors.targetSectionWordCount != null}>
                <FormLabel>
                  <Trans>Target Section Word Count</Trans>
                </FormLabel>
                <Controller
                  name="targetSectionWordCount"
                  control={control}
                  rules={{ required: t`Required`, min: { value: 100, message: t`Minimum 100` } }}
                  render={({ field }) => (
                    <NumberInput
                      min={100}
                      max={10000}
                      step={100}
                      value={field.value}
                      onChange={(_, valueAsNumber) => {
                        field.onChange(isNaN(valueAsNumber) ? 0 : valueAsNumber);
                      }}
                    >
                      <NumberInputField />
                      <NumberInputStepper>
                        <NumberIncrementStepper />
                        <NumberDecrementStepper />
                      </NumberInputStepper>
                    </NumberInput>
                  )}
                />
              </FormControl>

              {draftError != null ? (
                <Alert status="error" borderRadius="md" alignItems="start">
                  <AlertIcon />
                  <VStack align="start" spacing={1}>
                    <AlertDescription fontSize="sm">{draftError}</AlertDescription>
                    <Button
                      type="button"
                      variant="link"
                      size="sm"
                      fontWeight="normal"
                      onClick={() => {
                        setDraftError(null);
                        setStep('requirements');
                      }}
                    >
                      <Trans>Skip the draft and write the framework yourself</Trans>
                    </Button>
                  </VStack>
                </Alert>
              ) : null}

              {accredited ? (
                <Alert status="info" borderRadius="md">
                  <AlertIcon />
                  <Text fontSize="sm" fontWeight="medium">
                    <Trans>
                      Unit generation takes approximately 30 minutes, and pauses part way for an
                      allocation review.
                    </Trans>
                  </Text>
                </Alert>
              ) : null}
            </>
          ) : null}

          {step === 'fetching' ? (
            <Center flexDirection="column" gap={3} py={10}>
              {previewError == null ? (
                <>
                  <Spinner size="lg" />
                  <Text fontWeight="medium">
                    <Trans>Fetching from training.gov…</Trans>
                  </Text>
                  <Text fontSize="sm" color="fg.subtle">
                    <Trans>Parsing elements, performance criteria and evidence requirements</Trans>
                  </Text>
                </>
              ) : (
                <VStack spacing={3}>
                  <Alert status="error" borderRadius="md" data-testid={testIds.fetchError}>
                    <AlertIcon />
                    <AlertDescription>{previewError}</AlertDescription>
                  </Alert>
                  <Button
                    type="button"
                    size="sm"
                    onClick={() => void fetchPreview(getValues('unitCode'))}
                  >
                    <Trans>Try again</Trans>
                  </Button>
                </VStack>
              )}
            </Center>
          ) : null}

          {step === 'drafting' ? (
            <Center flexDirection="column" gap={3} py={10} textAlign="center">
              <Spinner size="lg" />
              <Text fontWeight="medium">
                <Trans>Drafting the course framework for {getValues('unitTitle')}…</Trans>
              </Text>
              <Text fontSize="sm" color="fg.subtle">
                {draftStage === 0 ? (
                  <Trans>Usually under a minute</Trans>
                ) : draftStage === 1 ? (
                  <Trans>Mapping out the unit requirements</Trans>
                ) : (
                  <Trans>Nearly there, finalising the framework</Trans>
                )}
              </Text>
            </Center>
          ) : null}

          {step === 'requirements' ? (
            <UnitRequirementsStep
              value={requirements}
              issues={issues}
              hidePerformanceEvidence={autoAssess}
              onChange={setRequirements}
            />
          ) : null}
        </ModalBody>

        <ModalFooter gap={2}>
          {step === 'drafting' ? null : step === 'details' ? (
            <>
              <Button key="cancel" type="button" variant="secondary" onClick={onClose}>
                <Trans>Cancel</Trans>
              </Button>
              <Button
                key="continue"
                type="submit"
                colorScheme="brand"
                data-testid={testIds.continueButton}
              >
                <Trans>Continue</Trans>
              </Button>
            </>
          ) : (
            <>
              {step === 'requirements' && blocking.length > 0 ? (
                <BlockersAlert
                  status="warning"
                  messages={blocking}
                  data-testid={testIds.validationMessage}
                />
              ) : null}

              <Button
                key="back"
                type="button"
                variant="secondary"
                onClick={goBackToDetails}
                data-testid={testIds.backButton}
              >
                <Trans>Back</Trans>
              </Button>

              {step === 'requirements' ? (
                <Tooltip key="generate" label={blocking[0]} isDisabled={blocking.length === 0}>
                  <Button
                    type="button"
                    colorScheme="brand"
                    leftIcon={<Sparkles size={16} />}
                    isDisabled={blocking.length > 0}
                    isLoading={triggerMutation.isPending}
                    data-testid={testIds.generateButton}
                    onClick={onGenerate}
                  >
                    <Trans>Generate unit</Trans>
                  </Button>
                </Tooltip>
              ) : null}
            </>
          )}
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
